import { gql, useFragment } from '@apollo/client';
import {
  CardDescription,
  CardHeader,
  CardTitle,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import { countBy, orderBy } from 'lodash';
import React, { useMemo } from 'react';

import * as orgRoutes from 'src/app/account/routes';
import { Loading } from 'src/components/common/loading/Loading';
import { SeatsLeftBadge } from 'src/components/seatsLeftBadge/SeatsLeftBadge';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import {
  ChampionDashboardCard,
  ChampionDashboardCardBody,
} from './ChampionDashboardCard';

export const MembersWidgetFragment = gql<
  GraphQLTypes.MembersWidgetFragment,
  Record<string, never>
>`
  fragment MembersWidgetFragment on Account {
    id
    limits {
      maxGraphOSSeats
    }
    availableRoles
    memberships {
      user {
        id
      }
      account {
        id
      }
      permission
    }
  }
`;

export const MembersWidget = ({ accountId }: { accountId: string }) => {
  const { data: account, complete } = useFragment({
    fragment: MembersWidgetFragment,
    from: {
      __typename: 'Account',
      id: accountId,
    },
  });

  const tableData = useMemo(() => {
    const counts = countBy(account?.memberships, (m) => m?.permission);
    return orderBy(
      Config.roleOrder.slice().reverse(),
      (role) => !account?.availableRoles?.includes(role),
    ).map((role) => ({
      role: Config.roleNameByRoleEnum[role],
      count:
        counts[role] ||
        (account?.availableRoles?.includes(role) ? (
          0
        ) : (
          <span className="text-sm">UNAVAILABLE</span>
        )),
    }));
  }, [account]);

  return (
    <ChampionDashboardCard className="col-span-full lg:col-span-3 xl:col-span-2">
      {!complete ? (
        <>
          <CardHeader>
            <CardTitle>Members</CardTitle>
          </CardHeader>
          <ChampionDashboardCardBody>
            <Loading />
          </ChampionDashboardCardBody>
        </>
      ) : (
        <>
          <CardHeader>
            <CardTitle>
              Members
              {!!account.limits?.maxGraphOSSeats && (
                <SeatsLeftBadge
                  accountId={account.id}
                  maxSeats={account.limits?.maxGraphOSSeats}
                  currentSeats={account.memberships?.length}
                />
              )}
            </CardTitle>
            {!!account.limits?.maxGraphOSSeats && (
              <CardDescription>
                There are <b>{account.memberships?.length ?? 0} members</b> in
                this organization.{' '}
                <Link
                  to={orgRoutes.members.location({
                    orgId: account.id,
                  })}
                >
                  Visit the members page
                </Link>{' '}
                for more details.
              </CardDescription>
            )}
          </CardHeader>
          <ChampionDashboardCardBody>
            <TableContainer>
              <Table size="condensed">
                <Thead>
                  <Tr>
                    <Th>Role</Th>
                    <Th>No. of Members</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {tableData.map(({ role, count }) => (
                    <Tr key={role}>
                      <Td>{role}</Td>
                      <Td>{count}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </ChampionDashboardCardBody>
        </>
      )}
    </ChampionDashboardCard>
  );
};
